import React from 'react';
import SiteNav from '@/components/SiteNav';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{title}</h2>
      <div className="mt-4 space-y-3 text-slate-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300" data-testid="terms-page">
      <SiteNav />

      <main className="max-w-3xl mx-auto px-6 pt-32 pb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>Terms of Service</h1>
        <p className="text-sm text-slate-500 mt-3">Last updated: July 2026</p>

        <div className="mt-10 space-y-4 text-lg leading-relaxed">
          <p>
            We tried to write these in plain language. By creating an EasyPeazy account or using
            our service, you agree to what's below. If something doesn't sit right, please reach us
            (see "Contact us") before you sign up.
          </p>
          <p className="text-base text-slate-400 border-l-2 border-indigo-500/40 pl-5">
            EasyPeazy is a managed website service. In these Terms, "EasyPeazy," "we," "us," and
            "our" mean the operator of the EasyPeazy service at easypeazy.site. "You" means the
            person or organization using the service.
          </p>
        </div>

        <Section title="Who can use EasyPeazy">
          <ul className="list-disc pl-6 space-y-2">
            <li>You are at least 18 years old (or the age of majority where you live) and able to enter a binding agreement.</li>
            <li>If you sign up on behalf of a business, church, or organization, you have authority to accept these Terms for it.</li>
            <li>You'll give us accurate information about yourself and your organization, and keep it up to date.</li>
          </ul>
        </Section>

        <Section title="Your account">
          <ul className="list-disc pl-6 space-y-2">
            <li>You're responsible for keeping your password safe and for activity under your account.</li>
            <li>Tell us right away if you think someone has gained access to your account.</li>
            <li>You may close your account at any time from your portal or by contacting support.</li>
          </ul>
        </Section>

        <Section title="What EasyPeazy provides">
          <p>
            EasyPeazy bundles the pieces of getting online into one managed service. Depending on
            what you buy, this can include:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>Searching for and registering a domain name through our domain registrar partner.</li>
            <li>Managed website hosting on our serverless platform, with a designed site you can edit yourself.</li>
            <li>Automated help creating and editing your pages.</li>
            <li>Optional professional email at your own domain.</li>
            <li>A customer portal for status, billing, and support.</li>
          </ul>
          <p className="mt-3">
            Some of these pieces are delivered through third-party providers (for example, a domain
            registrar, a hosting provider, a payment processor, an email provider, and a content-generation
            provider). Their terms may also apply to those pieces.
          </p>
        </Section>

        <Section title="Pricing, billing, and renewals">
          <ul className="list-disc pl-6 space-y-2">
            <li>Prices are shown before you buy. The price you see for a website bundle includes the pieces listed at checkout.</li>
            <li>Domains and hosting are typically billed on a yearly basis and, where you opt in, renew automatically so your site and domain don't lapse. You can turn off auto-renew or cancel before a renewal.</li>
            <li>Renewal prices can differ from a promotional first-year price. Where a renewal price is known, we try to show it up front so there are no surprises.</li>
            <li>Prices don't include taxes that may apply in your location.</li>
            <li>Payments are processed by our payment processor. We don't store your full card number.</li>
          </ul>
        </Section>

        <Section title="Refunds and cancellations">
          <p>
            You can cancel at any time. Because a domain registration is paid to a registrar at the
            moment it's registered, domain-registration fees are generally non-refundable once the
            domain has been registered in your name. Where we can offer a refund on a service fee,
            we'll say so at checkout or on request. If something went wrong on our end, contact us
            and we'll make it right.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>You agree not to use EasyPeazy, or a website we host for you, to:</p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>Break the law, infringe someone else's copyright or trademark, or violate anyone's rights.</li>
            <li>Distribute malware, run phishing or fraud, or send spam.</li>
            <li>Publish content that is sexually exploitative of minors, harassing, or that incites violence.</li>
            <li>Interfere with, overload, or attempt to gain unauthorized access to our systems or other customers' sites.</li>
          </ul>
          <p className="mt-3">
            We may suspend or remove content or a site that violates these rules, and we'll try to
            tell you why. When safety or the law requires it, we may act immediately.
          </p>
        </Section>

        <Section title="Your content and ownership">
          <p>
            Your website content — text, images, products, and the domain you register — belongs to
            you. You grant us the permission we need to host, store, back up, and display that
            content in order to run the service for you. The EasyPeazy name, software, and design
            remain ours; signing up doesn't give you a license to copy or resell them.
          </p>
        </Section>

        <Section title="Automated design features">
          <ul className="list-disc pl-6 space-y-2">
            <li>Our automated design tools can draft layouts and copy to get you started. They are computer-generated suggestions, not professional advice.</li>
            <li>Automated output can be wrong, generic, or off-brand. Please review anything before you publish it.</li>
            <li>You're responsible for the content you choose to publish on your site.</li>
          </ul>
        </Section>

        <Section title="Service availability and disclaimers">
          <p>
            We work hard to keep the service running, but EasyPeazy is provided "as is" and "as
            available." We don't promise the service will be uninterrupted or error-free, that a
            particular domain name will be available, or that third-party providers will never have
            outages. Scheduled maintenance and provider issues can happen.
          </p>
          <p className="mt-3">
            <strong>To the maximum extent allowed by law,</strong> our total liability for any claim
            arising from your use of EasyPeazy is limited to the greater of $100 or the amount you
            paid us in the 12 months before the claim. We aren't liable for indirect or consequential
            losses, or for lost profits or data.
          </p>
        </Section>

        <Section title="Suspension and termination">
          <p>
            We may suspend or close an account for repeated or serious violations of these Terms,
            for non-payment, or where the law requires it. Everywhere we reasonably can, we'll warn
            you first and give you a chance to fix the problem. If you close your account, your
            managed hosting stops at the end of your paid period; you keep any domain you registered
            through us for the term you paid for.
          </p>
        </Section>

        <Section title="Governing law and disputes">
          <p>
            These Terms are governed by the laws of the United States and the state where the EasyPeazy operator is established, without regard to conflict-of-laws rules. Before anything
            formal, both sides agree to first try to resolve a dispute directly and in good faith by
            contacting each other. You keep any rights you have to bring a claim in small-claims
            court.
          </p>
        </Section>

        <Section title="Changes to these Terms">
          <p>
            We may update these Terms from time to time. When we make a meaningful change, we'll
            update the "Last updated" date and, where appropriate, notify you. Continuing to use EasyPeazy after a change takes effect means you accept the updated Terms.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Questions about these Terms? Open a support ticket from your EasyPeazy portal, or email
            us at <em>support@easypeazy.site</em>.
          </p>
        </Section>
      </main>

      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-3xl mx-auto px-6 text-sm text-slate-500 flex justify-between">
          <Link to="/" className="inline-flex items-center min-h-[44px] hover:text-white transition-colors">&larr; Home</Link>
          <Link to="/privacy" className="inline-flex items-center min-h-[44px] hover:text-white transition-colors">Privacy Policy &rarr;</Link>
        </div>
      </footer>
    </div>
  );
}
